import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useEvent } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Home,
  Download,
  FileSpreadsheet,
  FileText,
  Printer,
  Calendar,
  Loader2,
  Check,
  CalendarPlus,
  ShieldCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExportPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentEvent, fetchEvent, getExportUrl, loading } = useEvent();
  const { isPro } = useAuth();
  
  const [pdfOrientation, setPdfOrientation] = useState('portrait');
  const [downloading, setDownloading] = useState({ pdf: false, excel: false, ical: false });

  useEffect(() => {
    if (eventId && !currentEvent) {
      fetchEvent(eventId).catch(err => {
        toast.error('Failed to load event');
        navigate('/');
      });
    }
  }, [eventId, currentEvent, fetchEvent, navigate]);

  const handleDownload = async (format) => {
    try {
      setDownloading(prev => ({ ...prev, [format]: true }));
      
      let url;
      let filename;
      if (format === 'ical') {
        url = `${API_URL}/api/events/${eventId}/export/ical`;
        filename = `${currentEvent?.name || 'schedule'}.ics`;
      } else {
        url = getExportUrl(eventId, format, { orientation: pdfOrientation });
        filename = `${currentEvent?.name || 'schedule'}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      }
      
      const token = localStorage.getItem('session_token');
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(`${format.toUpperCase()} download started`);
    } catch (err) {
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading && !currentEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-600 mb-4">Event not found</p>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200 no-print">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost"
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                className="text-slate-600"
              >
                <Home className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                  EXPORT SCHEDULE
                </h1>
                <p className="text-xs text-slate-500">{currentEvent.name}</p>
              </div>
            </div>
            <Button
              variant="outline"
              data-testid="view-schedule-btn"
              onClick={() => navigate(`/schedule/${eventId}`)}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* White-Label Banner for Pro */}
        {isPro && (
          <div className="flex items-center gap-3 mb-6 p-4 bg-[#00247D]/5 border border-[#00247D]/20 rounded-lg" data-testid="white-label-banner">
            <ShieldCheck className="w-5 h-5 text-[#00247D] flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#00247D]">White-Label Exports</p>
              <p className="text-xs text-slate-500">Your Pro exports are brand-free — no Schedule Guru logos or watermarks.</p>
            </div>
            <Badge className="bg-[#00247D] text-white ml-auto">PRO</Badge>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* PDF Export */}
          <Card className="border-slate-200 shadow-card no-print">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#CF142B]/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#CF142B]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                    PDF EXPORT
                  </h3>
                  <p className="text-sm text-slate-500 font-normal">Print-ready format</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium mb-3 block">Page Orientation</Label>
                <RadioGroup
                  value={pdfOrientation}
                  onValueChange={setPdfOrientation}
                  className="flex gap-4"
                >
                  <Label
                    htmlFor="portrait"
                    data-testid="pdf-portrait"
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                      pdfOrientation === 'portrait'
                        ? 'border-[#00247D] bg-[#00247D]/5'
                        : 'border-slate-200'
                    }`}
                  >
                    <RadioGroupItem value="portrait" id="portrait" />
                    <div className="w-6 h-8 border-2 border-current rounded"></div>
                    <span>Portrait</span>
                  </Label>
                  <Label
                    htmlFor="landscape"
                    data-testid="pdf-landscape"
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                      pdfOrientation === 'landscape'
                        ? 'border-[#00247D] bg-[#00247D]/5'
                        : 'border-slate-200'
                    }`}
                  >
                    <RadioGroupItem value="landscape" id="landscape" />
                    <div className="w-8 h-6 border-2 border-current rounded"></div>
                    <span>Landscape</span>
                  </Label>
                </RadioGroup>
              </div>
              
              <Button
                data-testid="download-pdf-btn"
                onClick={() => handleDownload('pdf')}
                disabled={downloading.pdf}
                className="w-full h-12 bg-[#CF142B] hover:bg-[#A80F22]"
              >
                {downloading.pdf ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download PDF
              </Button>
            </CardContent>
          </Card>

          {/* Excel Export */}
          <Card className="border-slate-200 shadow-card no-print">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#009E49]/10 flex items-center justify-center">
                  <FileSpreadsheet className="w-6 h-6 text-[#009E49]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                    EXCEL EXPORT
                  </h3>
                  <p className="text-sm text-slate-500 font-normal">Spreadsheet format (.xlsx)</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#009E49]" />
                  <span className="text-sm text-slate-700">Sheet 1: Full Schedule</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#009E49]" />
                  <span className="text-sm text-slate-700">Sheet 2: Teams & Standings</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#009E49]" />
                  <span className="text-sm text-slate-700">Auto-formatted columns</span>
                </div>
              </div>
              
              <Button
                data-testid="download-excel-btn"
                onClick={() => handleDownload('excel')}
                disabled={downloading.excel}
                className="w-full h-12 bg-[#009E49] hover:bg-[#008840]"
              >
                {downloading.excel ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download Excel
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* iCal + Print Row */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* iCal Export */}
          <Card className="border-slate-200 shadow-card no-print" data-testid="ical-export-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#00247D]/10 flex items-center justify-center">
                  <CalendarPlus className="w-6 h-6 text-[#00247D]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                    iCAL EXPORT
                  </h3>
                  <p className="text-sm text-slate-500 font-normal">Add to Google Calendar, Outlook, Apple</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#00247D]" />
                  <span className="text-sm text-slate-700">One event per match</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#00247D]" />
                  <span className="text-sm text-slate-700">Includes venue, court & stage</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#00247D]" />
                  <span className="text-sm text-slate-700">Compatible with all calendar apps</span>
                </div>
              </div>
              
              <Button
                data-testid="download-ical-btn"
                onClick={() => handleDownload('ical')}
                disabled={downloading.ical}
                className="w-full h-12 bg-[#00247D] hover:bg-[#001A5C]"
              >
                {downloading.ical ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download .ics File
              </Button>
            </CardContent>
          </Card>

          {/* Print Option */}
          <Card className="border-slate-200 shadow-card no-print">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Printer className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                    PRINT
                  </h3>
                  <p className="text-sm text-slate-500 font-normal">Open browser print dialog</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-700">Uses browser print dialog</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-700">Customize layout in print settings</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-700">Save as PDF from print dialog</span>
                </div>
              </div>
              
              <Button
                variant="outline"
                data-testid="print-btn"
                onClick={handlePrint}
                className="w-full h-12"
              >
                <Printer className="w-5 h-5 mr-2" />
                Print Schedule
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Preview Section */}
        <Card className="border-slate-200 shadow-card mt-6">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
              SCHEDULE PREVIEW
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="export-preview">
              {/* Header */}
              <div className="bg-[#00247D] text-white p-6">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                  {currentEvent.name}
                </h2>
                <p className="text-slate-300 mt-1">
                  Starting: {currentEvent.start_date}
                  {currentEvent.venue && ` | Venue: ${currentEvent.venue}`}
                </p>
              </div>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Time</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">{(currentEvent?.sport || '').toLowerCase() === 'soccer' ? 'Field' : 'Court'}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Stage</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                        {currentEvent.rules?.home_away_enabled ? 'Home' : 'Team A'}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">vs</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                        {currentEvent.rules?.home_away_enabled ? 'Away' : 'Team B'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEvent.schedule?.slice(0, 10).map((match, i) => {
                      const homeAwayEnabled = currentEvent.rules?.home_away_enabled;
                      const isHomeTeamA = match.is_home_team_a;
                      
                      let leftTeam, rightTeam;
                      if (homeAwayEnabled && isHomeTeamA !== null && isHomeTeamA !== undefined) {
                        leftTeam = isHomeTeamA ? (match.team_a_name || 'TBD') : (match.team_b_name || 'TBD');
                        rightTeam = isHomeTeamA ? (match.team_b_name || 'TBD') : (match.team_a_name || 'TBD');
                      } else {
                        leftTeam = match.team_a_name || 'TBD';
                        rightTeam = match.team_b_name || 'TBD';
                      }
                      
                      return (
                        <tr key={match.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-4 py-3 text-sm text-slate-700">{match.date || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{match.time || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 text-center">{match.court}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 text-center">{match.stage || '-'}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-800">{leftTeam}</td>
                          <td className="px-4 py-3 text-sm text-slate-400 text-center">vs</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-800">{rightTeam}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {currentEvent.schedule?.length > 10 && (
                <div className="px-4 py-3 text-center text-sm text-slate-500 bg-slate-50 border-t">
                  ... and {currentEvent.schedule.length - 10} more matches
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
